import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiEdit2, FiTrash2, FiFilter, FiDollarSign,
  FiTrendingUp, FiAlertCircle, FiCheckCircle
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import budgetService from '../../services/budgetService';
import CommonModal from '../../services/CommonModal';
import { FaFileExcel } from 'react-icons/fa';

const BudgetList = ({ budgets, onDelete, onRefresh, projects }) => {
  const [filters, setFilters] = useState({
    project_id: '',
    category: '',
    fiscal_year: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const confirmDelete = () => {
    onDelete(selectedBudget.id);
    setShowDeleteModal(false);
    setSelectedBudget(null);
  };

  useEffect(() => {
    if (budgets && budgets.length > 0) {
      calculateStatistics();
    }
  }, [budgets]);

  const calculateStatistics = () => {
    const totalAllocated = budgets.reduce((sum, budget) =>
      sum + parseFloat(budget.budget_allocated || 0), 0
    );
    const totalSpent = budgets.reduce((sum, budget) =>
      sum + parseFloat(budget.budget_spent || 0), 0
    );
    const totalRemaining = totalAllocated - totalSpent;
    const utilizationPercentage = totalAllocated > 0
      ? (totalSpent / totalAllocated) * 100
      : 0;

    setStatistics({
      totalAllocated: parseFloat(totalAllocated.toFixed(2)),
      totalSpent: parseFloat(totalSpent.toFixed(2)),
      totalRemaining: parseFloat(totalRemaining.toFixed(2)),
      utilizationPercentage: parseFloat(utilizationPercentage.toFixed(2)),
      totalBudgets: budgets.length
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    onRefresh(filters);
    setShowFilters(false);
  };

  const clearFilters = () => {
    const emptyFilters = {
      project_id: '',
      category: '',
      fiscal_year: ''
    };
    setFilters(emptyFilters);
    onRefresh(emptyFilters);
  };

  const handleDelete = async (id, budgetName) => {
    {
      try {
        await onDelete(id);
      } catch (error) {
        console.error('Error deleting budget:', error);
      }
    }
  };

  const getUtilizationColor = (percentage) => {
    if (percentage >= 90) return '';
    if (percentage >= 75) return '';
    if (percentage >= 50) return '';
    return '';
  };

  const getUtilizationBgColor = (percentage) => {
    if (percentage >= 90) return 'badge-danger';
    if (percentage >= 75) return 'badge-warning';
    if (percentage >= 50) return 'badge-info';
    return 'badge-success';
  };

  const handleDownloadBudgetList = () => {
    if (!budgets || budgets.length === 0) {
      toast.error('No budgets to download');
      return;
    }

    const headers = [
      'Project', 'Category', 'Allocated', 'Spent', 'Remaining', 'Utilization %', 'Fiscal Year'
    ];

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = budgets.map((budget) => {
      const utilization = budgetService.calculateUtilization(
        budget.budget_allocated,
        budget.budget_spent
      );
      const remaining = parseFloat(budget.budget_allocated) - parseFloat(budget.budget_spent);

      return [
        budget.project?.name || 'N/A',
        budget.category,
        budget.budget_allocated,
        budget.budget_spent,
        remaining,
        utilization,
        budget.fiscal_year || 'N/A'
      ];
    });

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `budgets-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Budget list downloaded');
  };

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Allocated</p>
                <p className="text-2xl font-bold text-gray-900">
                  {budgetService.formatCurrency(statistics.totalAllocated)}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <FiDollarSign className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Spent</p>
                <p className="text-2xl font-bold text-gray-900">
                  {budgetService.formatCurrency(statistics.totalSpent)}
                </p>
              </div>
              <div className="p-3  badge-warning rounded-full">
                <FiTrendingUp className="w-6 h-6" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Remaining</p>
                <p className="text-2xl font-bold text-gray-900">
                  {budgetService.formatCurrency(statistics.totalRemaining)}
                </p>
              </div>
              <div className={`p-3 rounded-full ${statistics.totalRemaining >= 0 ? 'badge-success' : 'badge-danger'
                }`}>
                {statistics.totalRemaining >= 0 ? (
                  <FiCheckCircle className="w-6 h-6 " />
                ) : (
                  <FiAlertCircle className="w-6 h-6" />
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Utilization</p>
                <p className={`text-2xl font-bold ${getUtilizationColor(statistics.utilizationPercentage)}`}>
                  {statistics.utilizationPercentage.toFixed(1)}%
                </p>
              </div>
              <div className={`p-3 rounded-full ${getUtilizationBgColor(statistics.utilizationPercentage)}`}>
                <FiTrendingUp className={`w-6 h-6 ${getUtilizationColor(statistics.utilizationPercentage)}`} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center text-gray-700 hover:text-gray-900"
          >
            <FiFilter className="mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button
            onClick={handleDownloadBudgetList}
            className="px-4 py-2 bg-white border border-green-600 text-green-600 rounded-lg flex items-center gap-2"
            title="Download budget list"
          >
            Download
            <FaFileExcel/>
          </button>
        </div>

        {showFilters && (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project
                </label>
                <select
                  name="project_id"
                  value={filters.project_id}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Projects</option>
                  {projects?.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  name="category"
                  value={filters.category}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Categories</option>
                  {budgetService.getCategories().map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fiscal Year
                </label>
                <input
                  type="number"
                  name="fiscal_year"
                  value={filters.fiscal_year}
                  onChange={handleFilterChange}
                  placeholder="e.g., 2024"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Clear
              </button>
              <button
                onClick={applyFilters}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>
      <CommonModal
        isOpen={showDeleteModal}
        title="Delete Budget"
        message="Are you sure you want to delete this budget?"
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedBudget(null);
        }}
        onConfirm={confirmDelete}
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Budget List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {budgets && budgets.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Allocated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Spent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Remaining
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fiscal Year
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {budgets.map((budget) => {
                  const utilization = budgetService.calculateUtilization(
                    budget.budget_allocated,
                    budget.budget_spent
                  );
                  const remaining = parseFloat(budget.budget_allocated) - parseFloat(budget.budget_spent);

                  return (
                    <tr key={budget.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {budget.project?.name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {budget.project?.code || ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${budgetService.getCategoryColor(budget.category)}-100 text-${budgetService.getCategoryColor(budget.category)}-800`}>
                          {budgetService.getCategoryIcon(budget.category)} {budget.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {budgetService.formatCurrency(budget.budget_allocated)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {budgetService.formatCurrency(budget.budget_spent)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-sm font-medium ${remaining >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {budgetService.formatCurrency(remaining)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-1 mr-2">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${utilization >= 90 ? 'bg-red-600' :
                                    utilization >= 75 ? 'bg-orange-600' :
                                      utilization >= 50 ? 'bg-yellow-600' :
                                        'bg-green-600'
                                  }`}
                                style={{ width: `${Math.min(utilization, 100)}%` }}
                              />
                            </div>
                          </div>
                          <span className={`text-sm font-medium ${getUtilizationColor(utilization)}`}>
                            {utilization}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {budget.fiscal_year || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link
                          to={`/app/budgets/${budget.id}/edit`}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          <FiEdit2 className="inline w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => {
                            setSelectedBudget(budget);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <FiTrash2 className="inline w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <FiDollarSign className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No budgets found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new budget.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetList;


